import React, { useState, useContext, createContext, useEffect, useCallback, useRef } from "react";
import { TextFileRecord, ImageFileRecord, GoogleDriveFile, TextAttachmentViewType } from "../types/files";
import useFileHandler from "../hooks/FileHandler/useFileHandler";
import { useChatContext } from "./AppDataContext";
import { retrieveGoogleDriveFile, retrieveGoogleDriveFiles } from "../services/api";
import { useAnalytics } from "../hooks/useAnalytics";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { chatConfig } from "../config/chatConfig";
import { countTotalAttachments } from "../hooks/FileHandler/utils";

interface TextViewerState {
  isOpen: boolean;
  file: TextFileRecord | null;
}

interface ImageModalState {
  isOpen: boolean;
  url: string;
  title: string;
}


interface AttachmentContextValue {
  textFiles: TextFileRecord[];
  imageFiles: ImageFileRecord[];
  processFiles: (
    e: React.ChangeEvent<HTMLInputElement>
    | { target: { files: File[] } }
    | { e: React.ChangeEvent<HTMLInputElement> }
  ) => void;
  handleAttachClick: (e: React.MouseEvent<HTMLButtonElement>, fileInputRef: React.RefObject<HTMLInputElement>) => void;
  removeTextFile: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, id: string) => void;
  removeImageFile: (id: string) => void;
  setTextFiles: React.Dispatch<React.SetStateAction<TextFileRecord[]>>;
  setImageFiles: React.Dispatch<React.SetStateAction<ImageFileRecord[]>>;
  isFileProcessing: boolean;
  imageModalState: ImageModalState;
  setImageModalState: React.Dispatch<React.SetStateAction<ImageModalState>>;
  handleImageClick: (file: File | string, title: string) => void;
  handleUploadedFileClick: (file: TextFileRecord | ImageFileRecord) => void;
  textViewerState: TextViewerState;
  setTextViewerState: React.Dispatch<React.SetStateAction<TextViewerState>>;
  closeTextViewer: () => void;
  closeImageModal: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isGoogleDriveModalOpen: boolean;
  setIsGoogleDriveModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  driveFolderStack: { id: string | null; name: string }[];
  handleGoogleDriveModalOpen: (files: GoogleDriveFile[], isLoading: boolean) => void;
  handleGoogleDriveModalClose: () => void;
  handleGoogleDriveFolderClick: (folder: GoogleDriveFile) => void;
  handleGoogleDriveNavigateUp: () => void;
  handleGoogleDriveNavigateRoot: () => void;
  googleDriveFiles: GoogleDriveFile[];
  handleGoogleDriveFileSelect: (file: GoogleDriveFile, isSelected: boolean) => void;
  isGoogleDriveFileSelected: (file: GoogleDriveFile) => boolean;
  processGoogleDriveFiles: () => void;
  isGoogleDriveFilesLoading: boolean;
  isGoogleDriveFileUploading: boolean;
  googleDriveError: string | null;
  setGoogleDriveFetchState: (files: GoogleDriveFile[], isLoading: boolean, error: string | null) => void;
  isTextAttachmentViewOpen: boolean;
  setIsTextAttachmentViewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreateDocumentClick: (type: TextAttachmentViewType) => void;
  handleTextAttachmentViewClose: () => void;
  handleAttachmentModalOpen: () => void;
  handleFileCardClick: (file: TextFileRecord) => void;
  textAttachmentViewType: TextAttachmentViewType | null;
  activeTextAttachmentFileId: string | null;
  setActiveTextAttachmentFileId: React.Dispatch<React.SetStateAction<string | null>>;
  addTextFile: (name: string) => void;
  updateTextFile: (id: string, content: string) => void;
}

const AttachmentContext = createContext<AttachmentContextValue | undefined>(undefined);

interface AttachmentProviderProps {
  children: React.ReactNode;
}

export const useAttachmentContext = () => {
  const context = useContext(AttachmentContext);
  if (context === undefined) {
    throw new Error('useAttachmentContext must be used within an AttachmentProvider');
  }
  return context;
};

export const AttachmentProvider: React.FC<AttachmentProviderProps> = ({ children }) => {
  const { messages, setSalesModalFeature, setShowSalesModal } = useChatContext();
  const { trackFileAttached, trackImageAttached } = useAnalytics();
  const auth = useAuth();
  const toast = useToast();
  const {
    textFiles,
    imageFiles,
    processFiles,
    handleAttachClick,
    removeTextFile,
    removeImageFile,
    setTextFiles,
    setImageFiles,
  } = useFileHandler({
    messages,
    setSalesModalFeature,
    setShowSalesModal,
  });
  const [isGoogleDriveModalOpen, setIsGoogleDriveModalOpen] = useState(false);
  const [isTextAttachmentViewOpen, setIsTextAttachmentViewOpen] = useState(false);
  const [driveFolderStack, setDriveFolderStack] = useState<{ id: string | null; name: string }[]>([{ id: null, name: 'My Drive' }]);
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [isGoogleDriveFilesLoading, setIsGoogleDriveFilesLoading] = useState(false);
  const [isGoogleDriveFileUploading, setIsGoogleDriveFileUploading] = useState(false);
  const [selectedGoogleDriveFiles, setSelectedGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [googleDriveError, setGoogleDriveError] = useState<string | null>(null);
  const [textAttachmentViewType, setTextAttachmentViewType] = useState<TextAttachmentViewType>('doc');
  const [activeTextAttachmentFileId, setActiveTextAttachmentFileId] = useState<string | null>(null);

  const setGoogleDriveFetchState = useCallback((files: GoogleDriveFile[], isLoading: boolean, error: string | null) => {
    setGoogleDriveFiles(files);
    setIsGoogleDriveFilesLoading(isLoading);
    setGoogleDriveError(error);
  }, []);

  const handleGoogleDriveModalOpen = useCallback((files: GoogleDriveFile[], isLoading: boolean) => {
    setDriveFolderStack([{ id: null, name: 'My Drive' }]);
    setIsGoogleDriveModalOpen(true);
    setGoogleDriveFiles(files);
    setIsGoogleDriveFilesLoading(isLoading);
    setGoogleDriveError(null);
    setSelectedGoogleDriveFiles([]);
  }, []);

  const handleGoogleDriveModalClose = useCallback(() => {
    setIsGoogleDriveModalOpen(false);
    setIsGoogleDriveFilesLoading(false);
    setDriveFolderStack([{ id: null, name: 'My Drive' }]);
    setSelectedGoogleDriveFiles([]);
    setGoogleDriveError(null);
  }, []);

  const handleGoogleDriveFileSelect = useCallback((file: GoogleDriveFile, isSelected: boolean) => {
    setSelectedGoogleDriveFiles(prev =>
      isSelected ? [...prev, file] : prev.filter(f => f.id !== file.id)
    );
  }, []);

  const isGoogleDriveFileSelected = useCallback((file: GoogleDriveFile) => {
    return selectedGoogleDriveFiles.some(f => f.id === file.id);
  }, [selectedGoogleDriveFiles]);

  const processGoogleDriveFiles = useCallback(async () => {
    setIsGoogleDriveFileUploading(true);
    try {
      // Retrieve blobs for all selected files, handling individual failures
      const results = await Promise.allSettled(
        selectedGoogleDriveFiles.map(file => retrieveGoogleDriveFile(file.id))
      );

      const filesToProcess: File[] = [];
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          const blob = result.value;
          const fileMeta = selectedGoogleDriveFiles[idx];
          const file = new File([blob], fileMeta.name, { type: fileMeta.mimeType });
          filesToProcess.push(file);
          
          // Track file attachment from Google Drive
          if (fileMeta.mimeType.startsWith('image/')) {
            trackImageAttached({
              imageType: fileMeta.mimeType,
              source: 'drag_drop' // Google Drive treated as drag_drop source
            });
          } else {
            trackFileAttached({
              fileType: fileMeta.mimeType,
              fileSize: blob.size,
              source: 'drag_drop'
            });
          }
        } else {
          console.error(`Error retrieving file blob for file ${selectedGoogleDriveFiles[idx].id} (${selectedGoogleDriveFiles[idx].name}):`, result.reason);
        }
      });

      if (filesToProcess.length > 0) {
        // Wrap in fake event for existing handler
        const syntheticEvent: { target: { files: File[] } } = { target: { files: filesToProcess } };
        processFiles(syntheticEvent);
      }

      // Clear selection and close modal
      setSelectedGoogleDriveFiles([]);
      handleGoogleDriveModalClose();
    } catch (error) {
      console.error('Error processing Google Drive files:', error);
      // Handle error, maybe show a message to the user
    } finally {
      setIsGoogleDriveFileUploading(false); // Indicate processing is finished
    }
  }, [selectedGoogleDriveFiles, processFiles, handleGoogleDriveModalClose, trackFileAttached, trackImageAttached]);

  const createTextFile = useCallback((name: string, type: TextAttachmentViewType): TextFileRecord | null => {
    if (!auth.user) {
      toast('Sign in to create documents', 'error');
      return null;
    }
    if (!auth.checkUsageData()) {
      toast('No messages remaining. Please upgrade your plan to create documents.', 'error');
      return null;
    }

    const pendingCount = textFiles.length + imageFiles.length;
    if (pendingCount >= chatConfig.limits.files.MAX_FILES_PER_MESSAGE) {
      toast(`You can only attach up to ${chatConfig.limits.files.MAX_FILES_PER_MESSAGE} files per message.`, 'error');
      return null;
    }

    const tier = auth.subscription?.plan?.toLowerCase();
    const validTiers = ['free', 'premium', 'ultimate'] as const;
    const normalizedTier = validTiers.includes(tier as typeof validTiers[number])
      ? tier as typeof validTiers[number]
      : 'free';
    const perChatLimit = chatConfig.limits.files.MAX_FILES_PER_CHAT[normalizedTier];
    const totalCount = countTotalAttachments(messages, false, []) + pendingCount;
    if (totalCount >= perChatLimit) {
      setSalesModalFeature('Additional File Uploads');
      setShowSalesModal(true);
      return null;
    }

    const newFile: TextFileRecord = {
      id: `create-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      size: 0,
      url: '',
      content: '',
      isLoaded: true,
      type,
      codeLanguage: type === 'code' ? 'javascript' : undefined,
    };
    setTextFiles(prev => [...prev, newFile]);
    setActiveTextAttachmentFileId(newFile.id);
    setTextAttachmentViewType(type);
    setIsTextAttachmentViewOpen(true);
    trackFileAttached({
      fileType: type,
      source: 'upload'
    });
    return newFile;
  }, [
    auth,
    imageFiles.length,
    messages,
    setSalesModalFeature,
    setShowSalesModal,
    setTextFiles,
    textFiles.length,
    toast,
    trackFileAttached,
  ]);

  const handleCreateDocumentClick = useCallback((type: TextAttachmentViewType) => {
    const extension = type === 'code' ? 'js' : 'md';
    const existingNames = new Set(textFiles.map(file => file.name.toLowerCase()));
    let name = `Untitled.${extension}`;
    let suffix = 2;
    while (existingNames.has(name.toLowerCase())) {
      name = `Untitled ${suffix++}.${extension}`;
    }
    createTextFile(name, type);
  }, [createTextFile, textFiles]);

  const handleAttachmentModalOpen = useCallback(() => {
    setIsTextAttachmentViewOpen(true);
  }, []);

  const handleTextAttachmentViewClose = useCallback(() => {
    setIsTextAttachmentViewOpen(false);
    setActiveTextAttachmentFileId(null);
    setTextAttachmentViewType('doc');
  }, []);

  const addTextFile = useCallback((name: string) => {
    createTextFile(name, textAttachmentViewType || 'doc');
  }, [createTextFile, textAttachmentViewType]);

  const updateTextFile = useCallback((id: string, content: string) => {
    setTextFiles(prev => {
      const prevFile = prev.find(f => f.id === id);
      if (!prevFile) return prev;

      if (prevFile.content === content) {
        return prev; // no changes
      }

      return prev.map(file => 
        file.id === id 
          ? { 
              ...file, 
              content: content,
            }
          : file
      );
    });
  }, [setTextFiles]);



  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isFileProcessing, setIsFileProcessing] = useState(false);

  const [imageModalState, setImageModalState] = useState({
    isOpen: false,
    url: '',
    title: ''
  });
  
  const [textViewerState, setTextViewerState] = useState<TextViewerState>({
    isOpen: false,
    file: null,
  });

  useEffect(() => {
    if (textFiles.some(file => !file.isLoaded) || imageFiles.some(file => !file.isLoaded)) {
      setIsFileProcessing(true);
    } else {
      setIsFileProcessing(false);
    }
  }, [textFiles, imageFiles]);

  // Sending or removing the active draft clears it from textFiles. Keep the
  // resource workspace state in sync so it closes cleanly instead of retaining
  // a stale file id behind the panel.
  useEffect(() => {
    if (
      isTextAttachmentViewOpen &&
      activeTextAttachmentFileId &&
      !textFiles.some(file => file.id === activeTextAttachmentFileId)
    ) {
      handleTextAttachmentViewClose();
    }
  }, [
    activeTextAttachmentFileId,
    handleTextAttachmentViewClose,
    isTextAttachmentViewOpen,
    textFiles,
  ]);

  // Tracks the most recent blob URL we created via URL.createObjectURL so we
  // can revoke it when the modal closes or a new image takes its place. Plain
  // string URLs (already-hosted images) are not tracked — we don't own them.
  const createdImageUrlRef = useRef<string | null>(null);

  const handleImageClick = useCallback((file: File | string, title: string) => {
    if (createdImageUrlRef.current) {
      URL.revokeObjectURL(createdImageUrlRef.current);
      createdImageUrlRef.current = null;
    }
    let url: string;
    if (file instanceof File) {
      url = URL.createObjectURL(file);
      createdImageUrlRef.current = url;
    } else {
      url = file;
    }
    setImageModalState({
      isOpen: true,
      url,
      title
    });
  }, []);

  const closeImageModal = useCallback(() => {
    if (createdImageUrlRef.current) {
      URL.revokeObjectURL(createdImageUrlRef.current);
      createdImageUrlRef.current = null;
    }
    setImageModalState({
      isOpen: false,
      url: '',
      title: ''
    });
  }, []);

  useEffect(() => {
    return () => {
      if (createdImageUrlRef.current) {
        URL.revokeObjectURL(createdImageUrlRef.current);
        createdImageUrlRef.current = null;
      }
    };
  }, []);

  const closeTextViewer = useCallback(() => {
    setTextViewerState({
      isOpen: false,
      file: null,
    });
  }, []);

  const handleUploadedFileClick = useCallback((file: TextFileRecord | ImageFileRecord) => {
    if (
      file.type === 'image/png' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg'
    ) {
      handleImageClick(file.url, file.name);
    } else if ('content' in file) {
      setTextViewerState({
        isOpen: true,
        file: file,
      });
    }
  }, [handleImageClick]);

  const handleGoogleDriveFolderClick = useCallback(async (folder: GoogleDriveFile) => {
    setDriveFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
    setIsGoogleDriveFilesLoading(true);
    setGoogleDriveError(null);
    try {
      const response = await retrieveGoogleDriveFiles(folder.id);
      if (response.success) {
        setGoogleDriveFetchState(response.files, false, null);
      } else {
        console.error(`Error loading folder ${folder.id}:`, response);
        setGoogleDriveFetchState([], false, response.message || `Failed to load folder contents for ${folder.name}.`);
      }
    } catch (error) {
      console.error(`Error loading folder ${folder.id}:`, error);
      setGoogleDriveFetchState([], false, `An error occurred while loading contents for ${folder.name}.`);
    }
  }, [setGoogleDriveFetchState]);

  const handleFileCardClick = useCallback((file: TextFileRecord) => {
    if (!file.isLoaded) return;
    setActiveTextAttachmentFileId(file.id);
    setTextAttachmentViewType(file.type === 'code' ? 'code' : 'doc');
    setIsTextAttachmentViewOpen(true);
  }, []);

  const handleGoogleDriveNavigateUp = useCallback(async () => {
    if (driveFolderStack.length <= 1) return;
    const parentIndex = driveFolderStack.length - 2;
    const parent = driveFolderStack[parentIndex];
    setDriveFolderStack(prev => prev.slice(0, prev.length - 1));
    setIsGoogleDriveFilesLoading(true);
    setGoogleDriveError(null);
    try {
      const response = await retrieveGoogleDriveFiles(parent.id ?? undefined);
      if (response.success) {
        setGoogleDriveFetchState(response.files, false, null);
      } else {
        console.error(`Error navigating up to folder ${parent.id}:`, response);
        setGoogleDriveFetchState([], false, response.message || `Failed to load contents for ${parent.name}.`);
      }
    } catch (error) {
      console.error(`Error navigating up to folder ${parent.id}:`, error);
      setGoogleDriveFetchState([], false, `An error occurred while loading contents for ${parent.name}.`);
    }
  }, [driveFolderStack, setGoogleDriveFetchState]);

  const handleGoogleDriveNavigateRoot = useCallback(async () => {
    setDriveFolderStack([{ id: null, name: 'My Drive' }]);
    setIsGoogleDriveFilesLoading(true);
    setGoogleDriveError(null);
    try {
      const response = await retrieveGoogleDriveFiles(undefined);
      if (response.success) {
        setGoogleDriveFetchState(response.files, false, null);
      } else {
        console.error('Error loading root folder:', response);
        setGoogleDriveFetchState([], false, response.message || 'Failed to load Google Drive files.');
      }
    } catch (error) {
      console.error('Error loading root folder:', error);
      setGoogleDriveFetchState([], false, 'An error occurred while loading Google Drive files.');
    }
  }, [setGoogleDriveFetchState]);

  return (
    <AttachmentContext.Provider value={{
      driveFolderStack,
      textFiles,
      imageFiles,
      processFiles,
      handleAttachClick,
      removeTextFile,
      removeImageFile,
      setTextFiles,
      setImageFiles,
      isFileProcessing,
      imageModalState,
      setImageModalState,
      handleImageClick,
      handleUploadedFileClick,
      textViewerState,
      setTextViewerState,
      closeTextViewer,
      closeImageModal,
      fileInputRef,
      isGoogleDriveModalOpen,
      setIsGoogleDriveModalOpen,
      googleDriveFiles,
      handleGoogleDriveModalOpen,
      handleGoogleDriveModalClose,
      handleGoogleDriveFolderClick,
      handleGoogleDriveNavigateUp,
      handleGoogleDriveNavigateRoot,
      handleGoogleDriveFileSelect,
      isGoogleDriveFileSelected,
      processGoogleDriveFiles,
      isGoogleDriveFilesLoading,
      isGoogleDriveFileUploading,
      googleDriveError,
      setGoogleDriveFetchState,
      isTextAttachmentViewOpen,
      setIsTextAttachmentViewOpen,
      handleCreateDocumentClick,
      handleTextAttachmentViewClose,
      handleFileCardClick,
      textAttachmentViewType,
      activeTextAttachmentFileId,
      setActiveTextAttachmentFileId,
      addTextFile,
      updateTextFile,
      handleAttachmentModalOpen
    } satisfies AttachmentContextValue}>
      {children}
    </AttachmentContext.Provider>
  );
};
